import type { TemplateRef } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  output,
  Renderer2,
  viewChild,
} from '@angular/core';

import { TranslatePipe } from '@core/i18n-legacy';
import { ReportComponentActionIntent, VisualizationActionsAvailability } from '@core/reports-types';
import {
  DropdownWidth,
  ConnectedOverlayDirective,
  DropdownComponent,
  IconButtonComponent,
  OverlayOriginDirective,
  DropdownHeaderComponent,
} from '@core/user-interface-legacy';

import { VisualizationActionsDropdownItemComponent } from './visualization-actions-dropdown-item/visualization-actions-dropdown-item.component';

@Component({
  selector: 'reports-visualization-actions',
  templateUrl: './visualization-actions.component.html',
  styleUrls: ['./visualization-actions.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IconButtonComponent,
    OverlayOriginDirective,
    ConnectedOverlayDirective,
    DropdownComponent,
    TranslatePipe,
    VisualizationActionsDropdownItemComponent,
    DropdownHeaderComponent,
  ],
})
export class VisualizationActionsComponent {
  private readonly elRef = inject(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly cdr = inject(ChangeDetectorRef);
  public readonly intents = ReportComponentActionIntent;
  public DropdownWidth = DropdownWidth;

  public availableActions = input.required<VisualizationActionsAvailability>();
  public failed = input.required<boolean>();
  public fullscreenAvailable = input<boolean>();

  public canDuplicate = computed(() => this.availableActions().duplicateAvailable);
  public canEdit = computed(() => this.availableActions().canEdit);
  public canMove = computed(() => this.availableActions().moveAvailable);
  public canExport = computed(() => this.availableActions().exportAvailable);
  public canDelete = computed(() => this.availableActions().deleteAvailable);

  public anyActionAvailable = computed(() =>
    Object.values(this.availableActions()).some(x => x === true),
  );

  public action = output<ReportComponentActionIntent>();
  public openFeatureGuard = output<void>();

  private dropdown = viewChild(DropdownComponent);
  private exportsTemplate = viewChild.required<TemplateRef<any>>('exportsTemplate');

  public actionClick(event: Event, action: ReportComponentActionIntent, upsell = false): void {
    if (upsell) {
      event.stopPropagation();
      this.openFeatureGuard.emit();
      return;
    }
    this.action.emit(action);
  }

  public forceVisibility(value: boolean) {
    value
      ? this.renderer.addClass(this.elRef.nativeElement, 'force-visible')
      : this.renderer.removeClass(this.elRef.nativeElement, 'force-visible');
  }

  public openExportsDropdown(event: Event) {
    this.dropdown()!.setNestedTemplate(this.exportsTemplate());
    event.stopPropagation();
    this.cdr.detectChanges();
  }

  public closeExportsDropdown(event: Event) {
    this.dropdown()!.prevTemplate();
    event.stopPropagation();
    this.cdr.detectChanges();
  }
}
